// Public profile at /user/:id — what a stranger sees when they click your
// name in a byline or find you via search. Read-only view + a "Send message"
// button that opens the composer for that user.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Avatar } from '../components/Avatar';
import { formatMastheadDate } from '../lib/format';

interface PublicUser {
    id:                string;
    display_name:      string;
    avatar_url?:       string | null;
    home_community_id?: string | null;
    created_at:        string;
    post_count:        number;
    group_count:       number;
}
interface FollowStats {
    followers: number;
    following: number;
    relation:  'none' | 'pending' | 'accepted' | 'self';
}

export function UserProfile() {
    const { id } = useParams<{ id: string }>();
    const api = useApi();
    const { authed, login } = useAuth();
    const navigate = useNavigate();
    const [user,  setUser]  = useState<PublicUser | null>(null);
    const [stats, setStats] = useState<FollowStats | null>(null);
    const [err,   setErr]   = useState('');

    const load = useCallback(async () => {
        if (!authed || !id) return;
        try {
            const [u, s] = await Promise.all([
                api.get<PublicUser>('/api/users/' + id),
                api.get<FollowStats>('/api/users/' + id + '/follow-stats').catch(() => null),
            ]);
            setUser(u);
            setStats(s);
        } catch (e) { setErr((e as Error).message); }
    }, [api, authed, id]);
    useEffect(() => { load(); }, [load]);

    async function toggleFollow() {
        if (!id || !stats) return;
        try {
            if (stats.relation === 'none') {
                const res = await api.post<{ status: 'pending' | 'accepted' }>('/api/users/' + id + '/follow');
                setStats({ ...stats, relation: res.status });
            } else {
                await api.del('/api/users/' + id + '/follow');
                setStats({ ...stats, relation: 'none',
                    followers: stats.relation === 'accepted' ? Math.max(0, stats.followers - 1) : stats.followers });
            }
        } catch (e) { alert((e as Error).message); }
    }

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see this profile.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (err) return <main className="profile-wrap"><p style={{ color: '#8b1919' }}>{err}</p></main>;
    if (!user) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;

    return (
        <main>
            <div className="profile-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <div style={{ display: 'flex', gap: 20, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Avatar src={user.avatar_url} name={user.display_name} seed={user.id} size={80} />
                        <div>
                            <h1 style={{ margin: 0 }}>{user.display_name}</h1>
                            <div className="issue">
                                Member since {new Date(user.created_at).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
                            </div>
                        </div>
                    </div>
                    <div className="rule-bottom" />
                </div>

                <section className="profile-stats">
                    <div className="stat"><strong>{user.post_count}</strong><span className="lbl">Posts</span></div>
                    <div className="stat"><strong>{user.group_count}</strong><span className="lbl">Groups</span></div>
                    {stats && <>
                        <div className="stat"><strong>{stats.followers}</strong><span className="lbl">Followers</span></div>
                        <div className="stat"><strong>{stats.following}</strong><span className="lbl">Following</span></div>
                    </>}
                </section>

                <section className="profile-block">
                    <h2>About</h2>
                    <dl className="kv">
                        <dt>Display name</dt><dd>{user.display_name}</dd>
                        <dt>Member since</dt><dd>{formatMastheadDate(new Date(user.created_at))}</dd>
                    </dl>
                </section>

                <section className="profile-block">
                    <div className="actions" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {stats && stats.relation !== 'self' && (
                            <button
                                type="button"
                                onClick={toggleFollow}
                                style={followBtnStyle(stats.relation)}
                            >
                                {followBtnLabel(stats.relation)}
                            </button>
                        )}
                        <button
                            type="button"
                            className="primary"
                            style={{ padding: '8px 18px', border: '1px solid var(--accent)', background: 'var(--accent)', color: 'white', fontFamily: '-apple-system, sans-serif', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer' }}
                            onClick={() => navigate('/messages?with=' + user.id)}
                        >
                            💬 Send a message
                        </button>
                        <Link to="/" className="plain" style={{ padding: '8px 18px', textDecoration: 'none', color: 'var(--ink)', border: '1px solid var(--ink)', fontFamily: '-apple-system, sans-serif', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>
                            ← Back to the paper
                        </Link>
                    </div>
                </section>
            </div>
        </main>
    );
}

function followBtnLabel(rel: FollowStats['relation']): string {
    switch (rel) {
        case 'accepted': return '✓ Following';
        case 'pending':  return '⋯ Requested';
        default:         return '+ Follow';
    }
}
function followBtnStyle(rel: FollowStats['relation']): React.CSSProperties {
    const base: React.CSSProperties = {
        padding: '8px 18px',
        fontFamily: '-apple-system, sans-serif',
        fontSize: 13, textTransform: 'uppercase', letterSpacing: 1,
        cursor: 'pointer',
    };
    if (rel === 'accepted') return { ...base, background: '#fff', color: 'var(--ink)', border: '1px solid var(--ink)' };
    if (rel === 'pending')  return { ...base, background: '#faf8f2', color: '#666', border: '1px dashed #999' };
    return { ...base, background: 'var(--ink)', color: '#fff', border: '1px solid var(--ink)' };
}
